import React, { useEffect, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Select, Tooltip, Button, message } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// COPY TO CLIPBOARD
import { CopyToClipboard } from "react-copy-to-clipboard";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateFilters } from "../../../redux/reports/reportsSlice";
// CUSTOM HOOKS
import useGetAllLiftsLight from "../../../customHooks/GET_requests/useGetAllLiftsLight";
// INTERFACES
import { RootState } from "../../../app/store";
import { LiftLightInterface } from "../../../interfaces/Lift.interface";
import { ReportSliceFiltersInterface } from "../../../interfaces/Reports.interface";

function ReportsFilterGetLifts() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Option, OptGroup } = Select;
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { filters, blockFilters } = useSelector(
    (state: RootState) => state.report
  );

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { liftsLight, isLoadingGetAllLiftsLight } = useGetAllLiftsLight();

  // ************************************************ */
  // GROUPED DATA *********************************** */
  const bankGroups = useMemo(() => {
    const bankMap = new Map<string, { id: string; name: string }>();
    liftsLight.forEach((lift: LiftLightInterface) => {
      if (lift.bank && !bankMap.has(lift.bank.id)) {
        bankMap.set(lift.bank.id, lift.bank);
      }
    });
    return Array.from(bankMap.values());
  }, [liftsLight]);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onChangeLift = (newValue: string) => {
    const copy: ReportSliceFiltersInterface = structuredClone(filters);
    const lift = liftsLight.find(
      (l: LiftLightInterface) => l.id === newValue
    );
    if (lift) {
      copy.liftId = lift.id;
      copy.liftName = lift.name;
      if (lift.bank) {
        copy.bankId = lift.bank.id;
        copy.bankName = lift.bank.name;
      }
    }
    dispatch(updateFilters(copy));
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (liftsLight.length > 0 && filters.liftId === "") {
      onChangeLift(liftsLight[0].id);
    }
  }, [liftsLight]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="generalStyles__width100" style={{ display: 'flex', alignItems: 'center' }}>
      <Button size="small" disabled style={{ minWidth: 'fit-content' }}>
        {t("report.selectLift")}
      </Button>
      <Tooltip title={liftsLight.length === 0 ? t("report.noLifts") : ""}>
        <Select
          className="generalStyles__width100"
          disabled={liftsLight.length === 0 || blockFilters}
          showSearch
          size="small"
          optionFilterProp="children"
          placeholder={t("report.selectLift")}
          loading={isLoadingGetAllLiftsLight}
          value={filters.liftId === "" ? null : filters.liftId}
          onChange={onChangeLift}
        >
          {bankGroups.map((group) => (
            <OptGroup key={group.id} label={`BANK / ${group.name}`}>
              {liftsLight
                .filter(
                  (lift: LiftLightInterface) =>
                    lift.bank?.id === group.id
                )
                .map((lift: LiftLightInterface) => (
                  <Option key={lift.id} value={lift.id}>
                    <Tooltip
                      overlayInnerStyle={{ minWidth: '380px' }}
                      placement="right"
                      title={
                        <div>
                          <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>{lift.name}</div>
                          <div style={{ borderBottom: '1px solid rgba(255,255,255,0.15)', marginBottom: '6px', paddingBottom: '6px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span><b className="generalStyles__info">ID:</b> <span style={{ fontSize: '11px', opacity: 0.85 }}>{lift.id}</span></span>
                              <CopyToClipboard text={lift.id} onCopy={() => message.success('Lift ID copied')}>
                                <FontAwesomeIcon icon={faCopy} style={{ cursor: 'pointer', marginLeft: '8px', fontSize: '11px', opacity: 0.7 }} />
                              </CopyToClipboard>
                            </div>
                          </div>
                          <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Bank</div>
                          <div><b className="generalStyles__info">{lift.bank?.name}</b></div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span><b className="generalStyles__info">ID:</b> <span style={{ fontSize: '11px', opacity: 0.85 }}>{lift.bank?.id}</span></span>
                            <CopyToClipboard text={lift.bank?.id || ''} onCopy={() => message.success('Bank ID copied')}>
                              <FontAwesomeIcon icon={faCopy} style={{ cursor: 'pointer', marginLeft: '8px', fontSize: '11px', opacity: 0.7 }} />
                            </CopyToClipboard>
                          </div>
                        </div>
                      }
                    >
                      {lift.name}
                    </Tooltip>
                  </Option>
                ))}
            </OptGroup>
          ))}
        </Select>
      </Tooltip>
    </div>
  );
}

export default ReportsFilterGetLifts;
